import React, { useState } from 'react'
import { Container, Row, Col, Card } from 'react-bootstrap'
import { CardRowItem } from '../../cardRowItem'
import { CardRowItemHideCurrencyIfNoValue } from '../../cardRowItemHideCurrencyIfNoValue'
import { AccordionSection, AccordionCard } from '../../accordion'
import { capitalizeOnlyFirstLetter, formatCurrency } from '../../../helpers'
import { useSelector } from 'react-redux'
import Tooltip from '../../tooltip'
import { PrimaryButton } from '@mmsg/louie'
import { SmallText } from 'components/styledComponents/text'
import { Modal } from 'react-bootstrap'
import { SummarySheet } from '../pdf/summarySheet'
import { Icon } from 'icons'
import { Emission } from '@mmsg/louie'
import * as E from './carSectionElements'
import { DoubleLineCardHeader } from 'components/styledComponents/doubleLineCardHeader/doubleLineCardHeader'
import { theme } from 'utils/theme'
import { VehicleDescriptionBlock } from 'components/styledComponents/vehicleDescriptionBlock'
import { ReactComponent as PinkTickIcon } from './../../../icons/pink-tick.svg'

const FeatureBlock = ({
  infoHeader,
  info1Content,
  info2Content,
  info3Content,
  maxWidth,
  theme,
  headerFontWeight,
}) => {
  return (
    <E.FeatureBlockContainer maxWidth={maxWidth} theme={theme}>
      {infoHeader && (
        <E.FeatureBlockHeader theme={theme} fontWeight={headerFontWeight}>
          {infoHeader}
        </E.FeatureBlockHeader>
      )}
      <div className="d-flex flex-column flex-md-row align-items-left justify-content-between">
        <E.FeatureBlockContent theme={theme}>
          <PinkTickIcon /> &nbsp;
          {info1Content}
        </E.FeatureBlockContent>
        {info2Content && (
          <E.FeatureBlockContent theme={theme}>
            <PinkTickIcon />
            &nbsp;
            {info2Content}
          </E.FeatureBlockContent>
        )}
        {info3Content && (
          <E.FeatureBlockContent theme={theme}>
            <PinkTickIcon />
            &nbsp;
            {info3Content}
          </E.FeatureBlockContent>
        )}
      </div>
    </E.FeatureBlockContainer>
  )
}

const CarAttribute = ({ title, value }) =>
  value ? (
    <Row>
      <Col className="font-weight-bold">{title}</Col>
      <Col className="text-right">{value}</Col>
    </Row>
  ) : null

const CarSection = ({
  data: {
    brand,
    year,
    make,
    family,
    engineSize,
    fuelType,
    transmission,
    style,
    variant,
    bodyColour,
    trimColour,
    onRoadcost,
    priveawayPrice,
    amTotalExGST,
    dealerOptionTotal,
    vehicleAccessories = [],
    tpoTotalExGST,
    listedPrice,
    luxuryCarTax,
    dealerDiscount,
    deliveryFee,
    procurementFee,
    vehicleAccessoriesNames,
    accesoriesByYou,
    gstCreditClaimable,
    totalVehicleExGST,
    totalExGSTNoVehicle,
    totalVehicleGST,
    vehicleId,
    vehicleImage,
    vehicleDescription,
    opportunityId,
    vehicleType,
    thirdPartyOption,
    isGov,
    cylinders,
    payload,
    cO2EmissionsPerKM,
    retailPrice,
  },
  applicationRole,
  expandAccordion,
  afterMarketAccessories,
  novatedLeaseData,
  pdfViewClick,
}) => {
  let estimateID = useSelector(
    (state) => state.individualEstimate.selectedEstimateId
  )
  const aftermarketOrderLines =
    afterMarketAccessories && afterMarketAccessories.aftermarketOrderLines

  const accessoriesByYou =
    thirdPartyOption && thirdPartyOption.thirdPartyOptionLines
  let hasSubmissionFailed = useSelector(
    (state) =>
      state.proceedEstimate && state.proceedEstimate.hasSubmissionFailed
  )

  const [showSummarySheet, setShowSummarySheet] = useState(false)
  const toggleviewSummarySheet = () => {
    setShowSummarySheet(!showSummarySheet)
  }
  const carSectionSubText =
    cO2EmissionsPerKM != null ? (
      <p>
        Take a look at your car specs, including CO<sub>2</sub> emissions.
      </p>
    ) : (
      <p>Take a look at your car specs.</p>
    )
  const emissionAttribute = (
    <p>
      CO<sub>2</sub> emissions
    </p>
  )

  return (
    <>
      <Modal show={showSummarySheet} onHide={toggleviewSummarySheet} size="lg">
        <Modal.Header
          closeButton
          style={{
            background: 'transparent',
            borderBottom: 'none',
            paddingBottom: 0,
          }}
        ></Modal.Header>
        <Modal.Body>
          <div>
            <SummarySheet novatedLeaseData={novatedLeaseData} exGst={true} />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'start' }}>
          <PrimaryButton extendInMob onClick={pdfViewClick}>
            View PDF
          </PrimaryButton>
          <PrimaryButton
            extendInMob
            invertColor
            onClick={toggleviewSummarySheet}
          >
            Close
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
      <Container className="section mt-5 mb-5">
        <div className="mt-3 mb-3 estimate-details__indicative">
          All estimates presented are for illustrative purposes only and are
          only current as at the date generated. Estimates shown may not be
          valid after the generation date.
        </div>
        <VehicleDescriptionBlock
          {...{
            vehicleDescription,
            vehicle: { id: estimateID, year, make, family, vehicleImage },
            isLargeImage: true,
          }}
        />

        {vehicleId && (
          <Card className="mb-13rem card-shadow">
            <Card.Header className="border-bottom-0">
              <FeatureBlock
                infoHeader={'The car details'}
                headerFontWeight={800}
                info1Content={
                  (engineSize === null ? '' : engineSize + ' ') +
                  fuelType +
                  ' engine'
                }
                info2Content={transmission}
                info3Content={style}
                theme={theme}
                //isTickPink={true}
              />
            </Card.Header>
            <Card.Body>
              <AccordionSection
                readerText="your car details"
                title={carSectionSubText}
                expandAccordion={expandAccordion}
              >
                <CarAttribute title="Series" value={variant} />
                <CarAttribute title="Engine" value={engineSize} />
                <CarAttribute title="Transmission" value={transmission} />
                <CarAttribute title="Body" value={style} />
                <CarAttribute title="Colour" value={bodyColour} />
                <CarAttribute title="Interior colour" value={trimColour} />
                <CarAttribute
                  title="Fuel"
                  value={capitalizeOnlyFirstLetter(fuelType)}
                />
                <CarAttribute title="Cylinders" value={cylinders} />
                <CarAttribute title="Payload" value={payload} />
                {cO2EmissionsPerKM != null && (
                  <div>
                    <CarAttribute
                      title={emissionAttribute}
                      value={cO2EmissionsPerKM + ' g/km'}
                    />
                    <E.EmissionContainer>
                      <Emission value={cO2EmissionsPerKM} />
                    </E.EmissionContainer>
                  </div>
                )}
              </AccordionSection>
            </Card.Body>
          </Card>
        )}

        <Card className="mb-13rem print-page-break-before card-shadow">
          <DoubleLineCardHeader
            priveawayPrice={priveawayPrice}
            retailPrice={retailPrice}
            vehicleDescription={vehicleDescription}
          />
          <Card.Body>
            <AccordionSection
              readerText="Your Oly novated leasing price"
              title="Unpack the car’s value, including accessories and on roads."
              expandAccordion={expandAccordion}
            >
              <>
                {/* Car Price: Start */}
                <Row>
                  <Col>Car price</Col>
                </Row>
                {listedPrice > 0 && (
                  <Row>
                    <Col className="col--nobold">Vehicle List Price</Col>
                    <Col className="text-right">
                      {formatCurrency(listedPrice)}
                    </Col>
                  </Row>
                )}
                {vehicleId && dealerDiscount > 0 && (
                  <Row>
                    <Col className="col--nobold  text-positive">
                      Fleet Discount
                    </Col>
                    <Col className="text-right  text-positive">
                      -{formatCurrency(dealerDiscount)}
                    </Col>
                  </Row>
                )}
                {/* <Row>
              <Col>Other discount</Col>
              <Col className="text-right">-$0,000.00</Col>
            </Row> */}
                {vehicleId && deliveryFee > 0 && (
                  <Row>
                    <Col className="col--nobold">Delivery charges</Col>
                    <Col className="text-right">
                      {formatCurrency(deliveryFee)}
                    </Col>
                  </Row>
                )}
                {vehicleId && procurementFee > 0 && (
                  <Row>
                    <Col className="col--nobold">Procurement fee</Col>
                    <Col className="text-right">
                      {formatCurrency(procurementFee)}
                    </Col>
                  </Row>
                )}
                {onRoadcost > 0 && (
                  <Row>
                    <Col className="col--nobold">On roads</Col>
                    <Col className="text-right">
                      {formatCurrency(onRoadcost)}
                    </Col>
                  </Row>
                )}
                {onRoadcost > 0 && (
                  <Row>
                    <Col
                      xs
                      md={9}
                      className="col--nobold font-color-indicative"
                    >
                      Included first year registration, stamp duty &amp; CTP if
                      applicable
                    </Col>
                  </Row>
                )}
                {luxuryCarTax > 0 && (
                  <Row>
                    <Col className="col--nobold">
                      <div className="d-flex ">
                        <span className="mr-2">Luxury car tax </span>
                        <Tooltip
                          header="Luxury car tax"
                          text="An Australian Government tax placed upon imported cars over a certain value. 33% tax is applied to the portion of the price that exceeds this value threshold. "
                        />{' '}
                      </div>
                    </Col>
                    <Col className="text-right ">
                      {formatCurrency(luxuryCarTax)}
                    </Col>
                  </Row>
                )}
                {/* Car Price: End */}

                {((aftermarketOrderLines && aftermarketOrderLines.length > 0) ||
                  (accessoriesByYou && accessoriesByYou.length > 0) ||
                  (vehicleAccessories && vehicleAccessories.length > 0)) && (
                  <div>
                    <hr></hr>

                    <CardRowItem title="Accessories" />
                    {vehicleAccessories && vehicleAccessories.length > 0 && (
                      <div>
                        <CardRowItem
                          title="Arranged by Dealership"
                          value={null}
                          mediumWeight
                        />
                        {vehicleAccessories &&
                          vehicleAccessories.map((item) => (
                            <div className="ml-3">
                              <CardRowItem
                                subtitle2={item.name}
                                value={item.amount}
                                isCurrency="true"
                                rightTextcss="text-right"
                              />
                            </div>
                          ))}
                      </div>
                    )}

                    {aftermarketOrderLines &&
                      aftermarketOrderLines.length > 0 && (
                        <div>
                          <CardRowItem
                            title="Arranged by Oly"
                            value={null}
                            mediumWeight
                            rightTextcss="text-right"
                          />
                          {aftermarketOrderLines.map((item) => (
                            <div className="ml-3">
                              <CardRowItem
                                subtitle2={item.description}
                                value={item.priceIncGST / 1.1}
                                isCurrency="true"
                                rightTextcss="text-right"
                              />
                            </div>
                          ))}
                        </div>
                      )}

                    {accessoriesByYou && accessoriesByYou.length > 0 && (
                      <div>
                        <CardRowItemHideCurrencyIfNoValue
                          title="Arranged by you"
                          value={null}
                          mediumWeight
                        />
                        {accessoriesByYou &&
                          accessoriesByYou.map((item) => (
                            <div className="ml-3">
                              <CardRowItemHideCurrencyIfNoValue
                                subtitle2={item.description}
                                value={item.amountExGST}
                                isCurrency="true"
                                rightTextcss="text-right"
                              />
                            </div>
                          ))}
                      </div>
                    )}
                    <SmallText>
                      (Accessories are all exclusive of GST for the purpose of
                      this estimate only.)
                    </SmallText>
                  </div>
                )}
                {vehicleId && (
                  <Row className="text-right border-top mx-0 pt-2 mt-3">
                    <Col xs md={9} className="px-0 col--nobold">
                      Subtotal (excl GST)
                    </Col>
                    <Col xs md={3} className="px-0">
                      {formatCurrency(totalVehicleExGST)}
                    </Col>
                  </Row>
                )}
                {!vehicleId && (
                  <Row className="text-right border-top mx-0 pt-2 mt-3">
                    <Col xs md={9} className="px-0 col--nobold">
                      Subtotal (excl GST)
                    </Col>
                    <Col xs md={3} className="px-0">
                      {formatCurrency(totalExGSTNoVehicle)}
                    </Col>
                  </Row>
                )}
                {totalVehicleGST > 0 && (
                  <Row className="text-right mx-0">
                    <Col xs md={9} className="px-0 col--nobold">
                      GST
                    </Col>
                    <Col xs md={3} className="px-0">
                      {formatCurrency(totalVehicleGST)}
                    </Col>
                  </Row>
                )}
                {gstCreditClaimable > 0 && (
                  <Row className="text-right mx-0">
                    <Col xs md={9} className="px-0 text-positive col--nobold">
                      GST savings upfront
                    </Col>
                    <Col xs md={3} className="px-0">
                      -{formatCurrency(gstCreditClaimable)}
                    </Col>
                  </Row>
                )}
                {priveawayPrice > 0 && (
                  <Row className="text-right font-weight-bold mx-0">
                    <Col xs md={9} className="px-0">
                      'Your Oly novated leasing price'
                    </Col>
                    <Col xs md={3} className="px-0 text-right--bold">
                      {formatCurrency(priveawayPrice)}
                    </Col>
                  </Row>
                )}
                {/* ACCESSORIES: End */}
                {/* Driving home your upfront savings: Start */}
                {gstCreditClaimable > 0 && (
                  <Row className="mt-4">
                    <Col xs={12} className="text-brand mb-2">
                      Driving home your upfront savings
                    </Col>
                    <Col xs={12}>
                      <p className="font-color-indicative col--nobold">
                        We've estimated you'll save{' '}
                        {formatCurrency(gstCreditClaimable)} before you even
                        start the ignition. Because, when it comes to your
                        driveaway price, novated leasing helps you save money
                        where you’d usually pay GST (on things like the listed
                        price, or even your accessories).
                      </p>
                    </Col>
                    <Col xs={12} className="font-weight-bold mt-4">
                      YOUR GST SAVINGS upfront{' '}
                      {formatCurrency(gstCreditClaimable)}
                    </Col>
                  </Row>
                )}
                {/* Driving home your upfront savings: End */}
              </>
            </AccordionSection>
          </Card.Body>
        </Card>
        {vehicleType && vehicleType === 'New' && (
          <AccordionCard
            title="How soon will I get my car?"
            expandAccordion={expandAccordion}
            className="card-shadow"
          >
            <div>
              If the car you’ve chosen is in stock, you could be behind the
              wheel in 2 to 5 business days after signing your lease. Get a more
              accurate idea by asking your consultant about the timeline for the
              car you’ve got your eye on.
            </div>
          </AccordionCard>
        )}
      </Container>
    </>
  )
}
export default CarSection
